import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Slider,
    Stack,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import PaletteIcon from '@mui/icons-material/Palette';
import Header from '../Header';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';
import { getErrorMessageKey } from '../../services/online/errorMessages';

/**
 * Dedicated "create room" screen — mirrors offline's GameSetup.jsx (Header +
 * Tabs-as-steps, same Container size) rather than a modal, per the user's
 * request. Table color / card back / animation speed are shown but disabled:
 * the online server doesn't orchestrate those yet (see TODO.md).
 */
export default function OnlineCreateRoomScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { authFetch } = useAuth();

    const [activeTab, setActiveTab] = useState(0);
    const [isPublic, setIsPublic] = useState(true);
    const [roomName, setRoomName] = useState('');
    const [gameMode, setGameMode] = useState('CLASSIC');
    const [gamePoints, setGamePoints] = useState(2);
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState(null);

    const handleCreateRoom = async () => {
        setError(null);
        setIsBusy(true);
        try {
            // gameType is "which game the very first round plays" — for
            // CLASSIC mode that's an arbitrary but fixed starting point
            // (Ronda first); for RONDA/BACIGA-only modes it's just the mode
            // itself, since that's the only game type this match ever plays.
            const gameType = gameMode === 'CLASSIC' ? 'RONDA' : gameMode;
            const room = await authFetch('/rooms', {
                method: 'POST',
                body: { gameType, gameMode, gamePoints, isPublic, name: roomName.trim() || undefined },
            });
            navigate(`/online/room/${room.id}`);
        } catch (err) {
            setError(t(getErrorMessageKey(err instanceof ApiError ? err.code : undefined)));
            setIsBusy(false);
        }
    };

    const tabs = [
        { label: t('online.create.tab_room'), icon: <MeetingRoomIcon /> },
        { label: t('online.create.tab_game'), icon: <SportsEsportsIcon /> },
        { label: t('online.create.tab_customize'), icon: <PaletteIcon /> },
    ];

    return (
        <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ flexShrink: 0 }}>
                <Header title={t('online.create.title')} showSettings={false} onBack={() => navigate('/online')} />
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}>
                <Container maxWidth={{ xs: 'sm', md: 'lg' }} sx={{ mt: { xs: 0.5, sm: 1 }, mb: { xs: 1, sm: 2 }, py: { xs: 0.75, sm: 1.5 } }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Card elevation={3}>
                        <Tabs
                            value={activeTab}
                            onChange={(_e, newValue) => setActiveTab(newValue)}
                            variant="fullWidth"
                            sx={{
                                borderBottom: '1px solid rgba(0,0,0,0.1)',
                                minHeight: { xs: 36, sm: 48 },
                                '@media (orientation: portrait)': { minHeight: 46 },
                                '& .MuiTab-root': {
                                    minHeight: { xs: 36, sm: 48 },
                                    py: { xs: 0.5, sm: 1 },
                                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                    fontWeight: 'bold',
                                    '@media (orientation: portrait)': { minHeight: 46, py: 1, fontSize: '0.8rem' }
                                },
                            }}
                        >
                            {tabs.map((tab, index) => (
                                <Tab
                                    key={index}
                                    icon={React.cloneElement(tab.icon, { sx: { fontSize: { xs: 16, sm: 20 } } })}
                                    iconPosition="start"
                                    label={tab.label}
                                />
                            ))}
                        </Tabs>

                        <CardContent sx={{ py: { xs: 1.5, sm: 2.5 }, px: { xs: 1.5, sm: 2.5 }, minHeight: { xs: 280, sm: 340 } }}>
                            {/* Tab 1: Sala (name + visibility) */}
                            {activeTab === 0 && (
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                            {t('online.create.name_label')}
                                        </Typography>
                                        <TextField
                                            value={roomName}
                                            onChange={(e) => setRoomName(e.target.value)}
                                            placeholder={t('online.create.name_placeholder')}
                                            fullWidth
                                            size="small"
                                            inputProps={{ maxLength: 60 }}
                                        />
                                    </Box>

                                    <Typography variant="subtitle2" fontWeight="bold">
                                        {t('online.create.visibility_label')}
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={isPublic ? 'PUBLIC' : 'PRIVATE'}
                                        exclusive
                                        onChange={(_e, value) => value && setIsPublic(value === 'PUBLIC')}
                                        fullWidth
                                    >
                                        <ToggleButton value="PUBLIC">{t('online.create.visibility_public')}</ToggleButton>
                                        <ToggleButton value="PRIVATE">{t('online.create.visibility_private')}</ToggleButton>
                                    </ToggleButtonGroup>
                                    <Typography variant="body2" color="text.secondary">
                                        {isPublic
                                            ? t('online.create.visibility_public_desc')
                                            : t('online.create.visibility_private_desc')}
                                    </Typography>
                                </Stack>
                            )}

                            {/* Tab 2: Juego (mode + points) */}
                            {activeTab === 1 && (
                                <Stack spacing={2.5}>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                            {t('setup.game_mode_label')}
                                        </Typography>
                                        <ToggleButtonGroup
                                            value={gameMode}
                                            exclusive
                                            onChange={(_e, value) => value && setGameMode(value)}
                                            fullWidth
                                        >
                                            <ToggleButton value="CLASSIC">{t('setup.game_mode_classic')}</ToggleButton>
                                            <ToggleButton value="RONDA">{t('online.lobby.game_type_ronda')}</ToggleButton>
                                            <ToggleButton value="BACIGA">{t('online.lobby.game_type_baciga')}</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                            {t('online.lobby.game_points_description')}
                                        </Typography>
                                        <ToggleButtonGroup
                                            value={gamePoints}
                                            exclusive
                                            onChange={(_e, value) => value && setGamePoints(value)}
                                            fullWidth
                                        >
                                            <ToggleButton value={2}>2</ToggleButton>
                                            <ToggleButton value={4}>4</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                </Stack>
                            )}

                            {/* Tab 3: Personalización (server doesn't orchestrate these yet) */}
                            {activeTab === 2 && (
                                <Stack spacing={2.5} sx={{ opacity: 0.5, pointerEvents: 'none' }}>
                                    <Alert severity="info" sx={{ opacity: 1 }}>
                                        {t('online.create.coming_soon_hint')}
                                    </Alert>

                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {t('settings.table_color')}
                                            </Typography>
                                            <Chip label={t('online.create.coming_soon')} size="small" />
                                        </Box>
                                        <Box sx={{ width: 60, height: 32, borderRadius: 1, bgcolor: '#257241', border: '1px solid #ccc' }} />
                                    </Box>

                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {t('settings.card_back')}
                                            </Typography>
                                            <Chip label={t('online.create.coming_soon')} size="small" />
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {['001', '002', '003'].map((back) => (
                                                <img
                                                    key={back}
                                                    src={`assets/images/deck/${back}.png`}
                                                    alt={back}
                                                    style={{ width: 32, height: 48, objectFit: 'cover', borderRadius: 4, border: '2px solid #ccc' }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {t('settings.animation_speed')}
                                            </Typography>
                                            <Chip label={t('online.create.coming_soon')} size="small" />
                                        </Box>
                                        <Slider value={1} min={0.2} max={3} step={0.1} disabled />
                                    </Box>
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Container>
            </Box>

            <Box
                sx={{
                    flexShrink: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    py: { xs: 0.75, sm: 1 },
                    px: { xs: 1, sm: 2 },
                }}
            >
                <Container maxWidth={{ xs: 'sm', md: 'lg' }}>
                    <Button variant="contained" fullWidth disabled={isBusy} onClick={handleCreateRoom}>
                        {t('online.lobby.create_room_button')}
                    </Button>
                </Container>
            </Box>
        </Box>
    );
}
